<template>
  <div class="toggle">
    <div
      class="image"
      :style="{ backgroundImage: `url(${toggle?.image})` }"
    ></div>
    <!-- eslint-disable-next-line vuejs-accessibility/label-has-for -->
    <label :id="toggle?.id" class="label">
      <span class="label-container">
        <x-icon v-if="toggle?.icon" :icon="toggle.icon" />
        <ys-text type="title6">
          {{ toggle?.name }}
        </ys-text>
      </span>
      <ys-text type="body2" class="description">
        {{ toggle?.description }}
      </ys-text>
    </label>
    <!-- eslint-disable-next-line vuejs-accessibility/click-events-have-key-events -->
    <div
      v-if="toggle?.hasAccess"
      :class="['input', { checked: isChecked }]"
      role="radio"
      tabindex="0"
      :aria-checked="isChecked"
      :aria-labelledby="toggle?.id"
      @click.stop="changeImagery(toggle)"
    >
      <div class="input-container">
        <input
          :id="toggle?.id"
          type="radio"
          name="imagery"
          :value="toggle?.id"
          :data-test="`${toggle?.id}-radio`"
          :checked="isChecked"
        />
      </div>
    </div>
    <ys-icon
      v-else
      icon="ysLock"
      size="medium"
      :data-test="`${toggle?.id}-lock`"
    />
  </div>
</template>
<script setup lang="ts">
///// imports /////
import { computed } from 'vue'
import type { UserLayer } from '@onxmaps/layer-management'
import '@onxmaps/yellowstone-ui/ys-text'
import '@onxmaps/yellowstone-ui/ys-icon'
import '@onxmaps/yellowstone-ui/icons/ysLock'
import xIcon from '@/components/shared/x-icon/index.vue'

import marketingEventService from '@/services/marketing-event-service'
import {
  triggerBasemapSelectedEvent,
  isHighFreqActive,
  BasemapImagery,
  basemapMarketingName,
} from '@/services/basemap-service'

interface Toggle extends Partial<UserLayer> {
  image: string
  icon: string | null
  turnImageryOn: () => void
}

interface Props {
  toggle: Toggle | null
}
///// props/emits /////
const props = withDefaults(defineProps<Props>(), {
  toggle: null,
})
///// refs and variables /////
const ImageryId = {
  Default: 'default',
  HighFrequency: BasemapImagery.HIGH_FREQ,
}
///// computed /////
const currentImagery = computed(() => {
  if (isHighFreqActive()) {
    return ImageryId.HighFrequency
  }
  return ImageryId.Default
})

const isChecked = computed(() => {
  return currentImagery.value === props.toggle?.id && props.toggle?.hasAccess
})

///// functions /////
const changeImagery = (toggle: Toggle | null) => {
  // If the user has access to that imagery, select it
  if (toggle?.hasAccess) {
    marketingEventService.setEventValues('basemap_selected', {
      active_basemap: basemapMarketingName.value,
    })
    toggle?.turnImageryOn()
    triggerBasemapSelectedEvent()
    return
  }
}
</script>
<style lang="scss" scoped>
$radio-size: 20px;
$radio-touch-size: 48px;

.toggle {
  border: 1px solid var(--ys-color-stroke-base);
  border-radius: 8px;
  display: flex;
  align-items: center;
  margin-bottom: var(--ys-spacing-150);
  overflow: hidden;
}

.image {
  width: 100px;
  height: 102px;
  background-size: contain;
}

.label {
  flex-basis: 55%;
  display: flex;
  flex-direction: column;
  padding: var(--ys-spacing-50) var(--ys-spacing-150) var(--ys-spacing-50)
    var(--ys-spacing-100);
}

.label-container {
  display: flex;
  align-items: center;
  gap: var(--ys-spacing-50);
}
.description {
  color: var(--ys-text-tertiary);
}

.input {
  width: auto;
  margin: 10px 0;
  display: inline-flex;
  justify-content: center;
  position: relative;
  box-sizing: content-box;
  cursor: pointer;
  &:focus {
    outline: none;
    .input-container {
      outline: 2px solid var(--ys-color-button-surface-primary-rest);
    }
  }
  .input-container {
    width: $radio-size;
    min-width: $radio-size;
    height: $radio-size;
    position: relative;
    border: 2px solid var(--ys-color-surface-tertiary);
    border-radius: 50%;
    text-align: left;

    &:focus {
      outline: none;
    }
    &:before,
    &:after {
      position: absolute;
      content: ' ';
    }
    &:before {
      width: $radio-touch-size;
      height: $radio-touch-size;
      top: 50%;
      left: 50%;
      z-index: 11;
      border-radius: 50%;
      transform: translate(-50%, -50%);
    }
    &:after {
      position: absolute;
      border-radius: 50%;
      opacity: 0;
      transform: scale3D(0.3, 0.3, 1);
      content: ' ';
    }
    input {
      position: absolute;
      left: -999em;
    }
  }
}

.input.checked {
  .input-container {
    background-color: var(--ys-color-button-surface-primary-rest);
    border-color: var(--ys-color-button-surface-primary-rest);
    &:after {
      opacity: 1;
      border: 10px solid var(--ys-color-button-text-primary-rest);
    }
  }
}
</style>
